import { useEffect, useState } from 'react';
import Dialog, {
  DefaultDialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/Dialog';
import {
  useDateFormatPreference,
  useDispatch,
  useInvestment,
  useWeekStartsOnPreference,
} from '@/hooks/useStore';
import { addInvestment as dispatchAddInvestment } from '@/store/slices/investmentsSlice';
import { useToggle } from '@/hooks/useToggle';
import { useAppState } from '@/hooks/useAppState';
import DateTimeInput from '@/components/DateTimeInput';
import NumberInput from '@/components/NumberInput';
import PriceInput from '@/components/PriceInput';
import { formatNumber, formatPrice, isBlank } from '@/utils/strings';
import { parseSharePrice } from '@/utils/reports';
import { showSuccessToaster } from '@/utils/toasters';
import { classes, createInvestment, updateInvestment } from './utils';

export type InvestmentDialogProps = {
  investmentId?: number;
  companyId?: number;
  investorId?: number;
  onClose: () => void;
};

export default function InvestmentDialog({
  investmentId,
  companyId,
  investorId,
  onClose,
}: InvestmentDialogProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const weekStartsOn = useWeekStartsOnPreference();
  const dateFormat = useDateFormatPreference();
  const investment = useInvestment(investmentId);
  const [date, setDate] = useState(new Date());
  const [sharePrice, setSharePrice] = useState('');
  const [shareCount, setShareCount] = useState('');
  const [isSaving, toggleSaving] = useToggle(false);

  useEffect(() => {
    if (investment) {
      setDate(new Date(investment.date));
      setSharePrice(formatPrice(parseSharePrice(investment)));
      setShareCount(formatNumber(investment.shareCount));
    }
  }, [investment]);

  const handleSave = async () => {
    try {
      toggleSaving(true);
      const data = { date, sharePrice, shareCount };
      if (investmentId) {
        const updatedInvestment = await updateInvestment({
          ...data,
          investmentId,
        });
        dispatch(dispatchAddInvestment(updatedInvestment));
      } else {
        const newInvestment = await createInvestment({
          ...data,
          companyId: companyId!,
          investorId: investorId!,
        });
        dispatch(dispatchAddInvestment(newInvestment));
      }
      showSuccessToaster('Investment updated');
      toggleSaving(false);
      onClose();
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error saving investment');
    }
  };

  const hasIds = !!(investmentId || (companyId && investorId));

  return (
    <Dialog className={classes.dialog} onClose={onClose}>
      <DialogTitle
        className={classes.dialogTitle}
        title={investmentId ? 'Edit investment' : 'New investment'}
        subtitle="Manage shares & investments"
        onClose={onClose}
      />
      <DialogBody className={classes.body}>
        <DateTimeInput
          disabled={isSaving}
          disablePast={false}
          showTime={false}
          className={classes.input}
          dateFormat={dateFormat}
          weekStartsOn={weekStartsOn}
          name="date"
          label="Date"
          value={date}
          onChange={(update) => setDate(update.date)}
        />
        <NumberInput
          disabled={isSaving}
          className={classes.input}
          value={shareCount}
          name="shareCount"
          label="Shares"
          onChange={(update) => setShareCount(update.shareCount)}
        />
        <PriceInput
          disabled={isSaving}
          className={classes.input}
          size="small"
          name="sharePrice"
          label="Share Price"
          value={sharePrice}
          onChange={setSharePrice}
        />
      </DialogBody>
      <DefaultDialogActions
        disabled={
          isSaving || !hasIds || isBlank(shareCount) || isBlank(sharePrice)
        }
        confirmLabel="Save"
        onConfirm={handleSave}
        onCancel={() => onClose()}
      />
    </Dialog>
  );
}
